import type { Timestamp } from 'firebase/firestore';
import type { UserProfile } from './index';

/**
 * Direct Messaging Types for Spirit of Shabbat
 *
 * This module defines types for the asymmetric DM system where
 * admin/leaders can initiate conversations with members, and
 * members can only reply (not initiate).
 */

// Attachment types for file uploads
export type AttachmentType = 'image' | 'file';

export interface Attachment {
  type: AttachmentType;
  url: string;
  name: string;
  size: number;
  mimeType: string;
  storagePath: string; // Firebase Storage path for deletion
}

// A conversation between an admin/leader and a member
export interface Conversation {
  id: string;
  participants: string[]; // Array of two user IDs
  initiatorId: string; // The admin/leader who started the conversation
  memberId: string; // The member being messaged
  createdAt: Timestamp;
  lastMessageAt: Timestamp;
  lastMessagePreview: string;
  lastMessageBy: string;
  lastMessageSenderId: string;
  // Encryption at rest: encrypted preview for participant views
  encryptedPreview?: string; // AES-256-GCM encrypted preview text
  previewIv?: string; // Base64 IV for preview decryption
  previewKeyVersion?: number; // Master key version
}

// Individual direct message within a conversation
export interface DirectMessage {
  id: string;
  conversationId: string;
  text: string; // Plain text (legacy) or decrypted text (runtime)
  senderId: string;
  senderName: string;
  timestamp: Timestamp;
  readAt: Timestamp | null;
  attachments: Attachment[]; // Plain array (legacy) or decrypted array (runtime)
  isDeleted: boolean;
  // Reaction summary (denormalized for fast reads)
  reactionSummary?: import('./reactions').ReactionSummary;
  // Quote/Reply fields
  quotedMessageId?: string;
  quotedMessage?: import('./index').QuotedMessage;
  // Encryption at rest fields
  encrypted?: boolean; // true = use ciphertext, false/absent = legacy plaintext
  ciphertext?: string; // Base64 AES-256-GCM encrypted JSON envelope
  iv?: string; // Base64 12-byte initialization vector
  keyVersion?: number; // Master key version (for rotation)
  hasAttachments?: boolean; // Plaintext flag for UI hints before decryption
}

// Per-user metadata for a conversation (stored in user's dm_metadata)
export interface ConversationMetadata {
  conversationId: string;
  unreadCount: number;
  lastReadAt: Timestamp | null;
  isArchived: boolean;
  isFavorite: boolean;
  isMuted: boolean;
  isBlocked: boolean; // Only admin/leaders can block member replies
}

// Combined view of a conversation with metadata and other user info
export interface DMContact {
  conversation: Conversation;
  metadata: ConversationMetadata;
  otherUser: UserProfile;
  isTyping: boolean;
}

// Typing status stored in Firestore for real-time updates
export interface TypingStatus {
  isTyping: boolean;
  userId: string;
  timestamp: Timestamp;
}

// Input for creating a new message
export interface SendMessageInput {
  text: string;
  attachments?: Attachment[];
  quotedMessageId?: string;
  quotedMessage?: import('./index').QuotedMessage;
  /** Client-generated ID for correlating optimistic updates with server messages */
  clientId?: string;
}

// Input for creating a new conversation
export interface CreateConversationInput {
  initiatorId: string;
  memberId: string;
  initialMessage?: string;
}

// Filter options for conversation list
export type ConversationFilter = 'all' | 'unread' | 'archived' | 'favorites';

// Sort options for conversation list
export type ConversationSort = 'recent' | 'unread' | 'alphabetical';

// DM Context state
export interface DMState {
  conversations: DMContact[];
  activeConversationId: string | null;
  totalUnreadCount: number;
  loading: boolean;
  error: string | null;
}

// DM Context actions
export interface DMContextValue extends DMState {
  // Navigation
  openConversation: (conversationId: string) => void;
  closeConversation: () => void;

  // Messaging
  sendMessage: (conversationId: string, message: SendMessageInput) => Promise<void>;
  markAsRead: (conversationId: string) => Promise<void>;
  deleteMessage: (conversationId: string, messageId: string) => Promise<void>;

  // Conversation management
  startConversation: (memberId: string, initialMessage?: string) => Promise<string>;
  archiveConversation: (conversationId: string) => Promise<void>;
  unarchiveConversation: (conversationId: string) => Promise<void>;
  toggleFavorite: (conversationId: string) => Promise<void>;
  muteConversation: (conversationId: string, muted: boolean) => Promise<void>;
  blockUser: (conversationId: string, blocked: boolean) => Promise<void>;

  // Typing indicators
  setTyping: (conversationId: string, isTyping: boolean) => void;

  // Refresh
  refreshConversations: () => Promise<void>;
}

// Admin view of all conversations (for oversight)
export interface AdminConversationView {
  conversation: Conversation;
  initiator: UserProfile;
  member: UserProfile;
  messageCount: number;
  lastActivity: Timestamp;
}

// File upload constraints
export const DM_ATTACHMENT_LIMITS = {
  maxFileSize: 10 * 1024 * 1024, // 10 MB
  allowedImageTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  allowedDocumentTypes: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  get allowedTypes() {
    return [...this.allowedImageTypes, ...this.allowedDocumentTypes];
  },
} as const;

// Helper to check if file is allowed
export function isAllowedAttachment(file: File): boolean {
  return (
    file.size <= DM_ATTACHMENT_LIMITS.maxFileSize &&
    (DM_ATTACHMENT_LIMITS.allowedTypes as readonly string[]).includes(file.type)
  );
}

// Helper to get attachment type from mime type
export function getAttachmentType(mimeType: string): AttachmentType {
  return (DM_ATTACHMENT_LIMITS.allowedImageTypes as readonly string[]).includes(mimeType)
    ? 'image'
    : 'file';
}
